import * as React from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/button";
import { Table, TableWrapper } from "@/components/ui/table";
import { StatCard } from "@/components/data-display/stat-card";
import { PageHeader } from "@/components/data-display/page-header";
import { InfoBanner } from "@/components/ui/banner";
import { toast } from "@/components/ui/toast";
import { localOnly } from "@/lib/fixture-notice";
import { cn } from "@/lib/utils";
import { usePermission } from "@/hooks/use-permission";
import {
  TRS_HOME_METRICS,
  TRS_OPPORTUNITIES, TRS_OPP_QUOTE_STATE, TRS_INTENT_0051, TRS_ROUTES, TRS_ROUTE_INTRO, TRS_CALENDAR,
  STK_POSITIONS, STK_OVERVIEW_METRICS, STK_CREATE_INTENT_FORM, STK_VALIDATOR_LIDO, STK_REWARDS, STK_UNSTAKE_004,
  LND_POSITIONS, LND_OVERVIEW_METRICS, LND_SUPPLY_INTENT, LND_BORROW_INTENT, LND_DETAIL_002, LND_MARGIN_INCIDENT,
} from "./treasury-data";
import type { TrsOppType, TrsOppStatus, TrsOpportunity, StkPositionStatus, TrsIntentTimelineStep, TrsCalendarEvent, LndPosition } from "./treasury-data";
import { Modal, BackLink, ToneBadge, PositionStatus, TreasuryBanner } from "./treasury-ui";
import type { TreasuryTone } from "./treasury-data";

/* ─── TRS-00 Treasury Home ───
   Canonical source: specs/06-treasury-operations/mocks/treasury-home-wireframes.html#TRS-00
   ("happy" state only — exception/permission/degraded/empty/readonly state
   tabs exist in the wireframe but are not implemented here; see the TRS-00
   audit section for the reasoning). Metric values, risk-flag subject, and
   the Top Staking Positions / Venue Balances rows are TRS_HOME_METRICS
   verbatim. Open Opportunities reuses the existing TRS_OPPORTUNITIES
   registry (TRS-01) — no duplicate opportunity data was created. */
export function TreasuryOverview() {
  const M = TRS_HOME_METRICS;
  const deferTo = (frame: string, label: string) =>
    toast({
      variant: "info",
      title: `${label} — not implemented`,
      description: `${frame} is deferred to a later treasury task. No navigation was made; nothing was created or executed.`,
    });

  return (
    <div>
      <PageHeader title="Treasury" subtitle="Overview of AUM, idle balances, active positions, venue exposure, and risk flags." />

      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-5">
        <StatCard value={M.totalAum} label="Total AUM" hint={M.aumWallets} />
        <StatCard value={M.idleBalance} label="Idle Balances" hint={M.idlePct} />
        <HomeMetricCard
          value={M.activeStaking}
          label="Active Staking"
          sub={<>{M.stakingCount} · <Link to="/treasury/staking" className="font-semibold text-primary hover:underline">View staking →</Link></>}
        />
        <HomeMetricCard
          value={M.activeLending}
          label="Active Lending"
          sub={<>{M.lendingCount} · <button type="button" className="font-semibold text-primary hover:underline" onClick={() => deferTo("LND-00", "Lending Overview")}>View lending →</button></>}
        />
        <HomeMetricCard
          value={M.venueExposure}
          label="Venue Exposure"
          sub={<>{M.venueCount} · <button type="button" className="font-semibold text-primary hover:underline" onClick={() => deferTo("LIQ-00", "Liquidity Dashboard")}>View liquidity →</button></>}
        />
      </div>

      <InfoBanner variant="warning">
        <strong>Risk Flag:</strong> {M.riskFlag.subject}. Review collateral position.{" "}
        <button type="button" className="font-semibold underline" onClick={() => deferTo("LND-00", "Lending Overview")}>View position →</button>
      </InfoBanner>

      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-base font-bold text-ink">Open Opportunities</h2>
        <span className="rounded-full border border-info-border bg-info-soft px-2 py-0.5 text-[11px] font-bold text-info">{TRS_OPPORTUNITIES.length}</span>
      </div>
      <TableWrapper className="mb-5">
        <Table className="dense">
          <thead><tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted"><th className="px-4 py-2.5">Opportunity</th><th className="px-4 py-2.5">Type</th><th className="px-4 py-2.5">Est. Yield</th><th className="px-4 py-2.5">Risk</th><th className="px-4 py-2.5 text-right">Action</th></tr></thead>
          <tbody>
            {TRS_OPPORTUNITIES.map((o) => (
              <tr key={o.id} className="border-b border-line last:border-b-0">
                <td className="px-4 py-3 text-[13px] font-medium text-ink">{o.description}</td>
                <td className="px-4 py-3"><ToneBadge tone={OPP_TYPE_TONE[o.type]}>{o.type}</ToneBadge></td>
                <td className={cn("px-4 py-3 text-[13px] font-bold tabular-nums", o.estYield === "—" ? "text-muted" : "text-success")}>{o.estYield}</td>
                <td className="px-4 py-3"><ToneBadge tone={OPP_RISK_TONE[o.risk]}>{o.risk}</ToneBadge></td>
                <td className="px-4 py-3 text-right"><Link to="/treasury/opportunities" className={buttonVariants({ size: "sm" })}>Review</Link></td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>

      <div className="grid gap-5 lg:grid-cols-2">
        <Panel title="Top Staking Positions" bodyClassName="p-0">
          <TableWrapper className="rounded-none border-0">
            <Table className="dense">
              <thead><tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted"><th className="px-4 py-2.5">Asset</th><th className="px-4 py-2.5">Amount</th><th className="px-4 py-2.5">APY</th><th className="px-4 py-2.5">Wallet</th></tr></thead>
              <tbody>
                {M.topStaking.map((t) => (
                  <tr key={t.wallet} className="border-b border-line last:border-b-0">
                    <td className="px-4 py-3 text-[13px] font-bold text-ink">{t.asset}</td>
                    <td className="px-4 py-3 text-[13px] tabular-nums text-text">{t.amount}</td>
                    <td className="px-4 py-3 text-[13px] font-bold tabular-nums text-success">{t.apy}</td>
                    <td className="px-4 py-3"><Link to={`/wallets/${t.wallet}`} className="font-mono text-[13px] font-semibold text-primary hover:underline">{t.wallet}</Link></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        </Panel>
        <Panel title="Venue Balances" bodyClassName="p-0">
          <TableWrapper className="rounded-none border-0">
            <Table className="dense">
              <thead><tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted"><th className="px-4 py-2.5">Venue</th><th className="px-4 py-2.5">Balance</th><th className="px-4 py-2.5">% of AUM</th></tr></thead>
              <tbody>
                {M.venueBalances.map((v) => (
                  <tr key={v.venueId} className="border-b border-line last:border-b-0">
                    <td className="px-4 py-3 text-[13px]">
                      <button type="button" className="font-semibold text-primary hover:underline" onClick={() => deferTo("LIQ-00", "Liquidity Dashboard")}>{v.venueId} · {v.venue}</button>
                    </td>
                    <td className="px-4 py-3 text-[13px] tabular-nums text-text">{v.balance}</td>
                    <td className="px-4 py-3 text-[13px] tabular-nums text-text">{v.pct}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        </Panel>
      </div>

      <TreasuryBanner label="TRS-00: Treasury Dashboard" />
    </div>
  );
}

function HomeMetricCard({ value, label, sub }: { value: string; label: string; sub?: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-line bg-surface px-5 py-[18px]">
      <div className="text-[28px] font-bold leading-[1.1] tracking-tight text-ink">{value}</div>
      <div className="mt-1 text-xs text-muted">{label}</div>
      {sub && <div className="mt-0.5 text-xs text-muted">{sub}</div>}
    </div>
  );
}

function Panel({ title, bodyClassName, children, actions }: { title?: React.ReactNode; bodyClassName?: string; children: React.ReactNode; actions?: React.ReactNode }) {
  return (
    <section className="mb-5 overflow-hidden rounded-lg border border-line bg-surface">
      {title && <header className="border-b border-line px-5 py-3 flex items-center justify-between"><h3 className="text-[15px] font-bold text-ink">{title}</h3>{actions}</header>}
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

/* ─── TRS-01 Opportunity Queue ─── */
const OPP_TYPE_TONE: Record<TrsOppType, TreasuryTone> = { Stake: "success", Lend: "info", Rebalance: "warning", Swap: "neutral" };
const OPP_RISK_TONE: Record<"Low" | "Medium", TreasuryTone> = { Low: "success", Medium: "warning" };
const OPP_STATUS_TONE: Record<TrsOppStatus, TreasuryTone> = { New: "info", Suggested: "neutral" };
const OPP_TYPES: TrsOppType[] = ["Stake", "Lend", "Rebalance", "Swap"];

const oppYieldNum = (o: TrsOpportunity) => {
  const m = o.estYield.match(/([\d.]+)/);
  return m ? Number.parseFloat(m[1]) : -1;
};

function OppSourceCell({ o }: { o: TrsOpportunity }) {
  const m = o.source.match(/^(WLT-\d{3})/);
  if (!m) return <span className="text-[13px] text-text">{o.source}</span>;
  return (
    <span className="text-[13px] text-text">
      <Link to={`/wallets/${m[1]}`} className="font-mono font-semibold text-primary hover:underline">{m[1]}</Link>
      {" "}{o.source.slice(m[0].length).trim()}
    </span>
  );
}

export function OpportunityQueue() {
  const QUOTE = TRS_OPP_QUOTE_STATE.expired;
  const [refreshMap, setRefreshMap] = React.useState<Record<string, boolean>>({});
  const quoteExpired = !refreshMap[QUOTE.oppId];

  const [typeFilter, setTypeFilter] = React.useState<TrsOppType | "ALL">("ALL");
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [sortByYield, setSortByYield] = React.useState(false);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [dismissed, setDismissed] = React.useState<Set<string>>(new Set());
  const [intentOpp, setIntentOpp] = React.useState<TrsOpportunity | null>(null);

  const rows = React.useMemo(() => {
    let list = TRS_OPPORTUNITIES.filter((o) => !dismissed.has(o.id));
    if (typeFilter !== "ALL") list = list.filter((o) => o.type === typeFilter);
    if (sortByYield) list = [...list].sort((a, b) => oppYieldNum(b) - oppYieldNum(a));
    return list;
  }, [dismissed, typeFilter, sortByYield]);

  const openCount = TRS_OPPORTUNITIES.length - dismissed.size;
  const expiredRowVisible = quoteExpired && rows.some((o) => o.id === QUOTE.oppId);

  const toggleRow = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const refreshQuote = () => {
    setRefreshMap((m) => ({ ...m, [QUOTE.oppId]: true }));
    toast({
      variant: "info",
      title: "Quote refresh (static fixture)",
      description: "Fetching a live Lido staking quote requires the quoting service — API/backend dependency. No live quote was actually obtained.",
    });
  };

  const dismissSelected = () => {
    const count = selected.size;
    setDismissed((prev) => {
      const next = new Set(prev);
      selected.forEach((id) => next.add(id));
      return next;
    });
    setSelected(new Set());
    toast({
      variant: "info",
      title: `${count} opportunity${count === 1 ? "" : "s"} dismissed`,
      description: "Client-side fixture behavior only — dismissals persist for this session.",
    });
  };

  const stageIntent = () => {
    if (!intentOpp) return;
    toast({ variant: "success", title: `${intentOpp.id} intent staged`, description: "Full intent creation, policy evaluation and approval routing are API/backend dependencies — nothing was executed." });
    setIntentOpp(null);
  };

  return (
    <div>
      <PageHeader title="Opportunity Queue" subtitle="Actionable treasury opportunities: stake, lend, borrow, swap, liquidity provision, and rebalancing." />
      {expiredRowVisible && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-danger-border bg-danger-soft p-3 text-sm text-danger">
          <strong>Quote expired:</strong>
          <span>{QUOTE.oppId} Lido staking quote expired at {QUOTE.quoteExpiredAt}. Refresh to obtain a new quote.</span>
        </div>
      )}
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-base font-bold text-ink">Opportunity Queue</h2>
        <span className="rounded-full border border-info-border bg-info-soft px-2 py-0.5 text-[11px] font-bold text-info">{openCount} open</span>
      </div>
      <TableWrapper>
        <Table className="dense">
          <thead><tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted"><th className="px-4 py-2.5">ID</th><th className="px-4 py-2.5">Type</th><th className="px-4 py-2.5">Description</th><th className="px-4 py-2.5">Est. Yield</th><th className="px-4 py-2.5">Risk</th><th className="px-4 py-2.5">Source</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5 text-right">Action</th></tr></thead>
          <tbody>
            {rows.map((o) => {
              const isSelected = selected.has(o.id);
              const isExpired = o.id === QUOTE.oppId && quoteExpired;
              return (
                <tr
                  key={o.id}
                  tabIndex={0}
                  aria-pressed={isSelected}
                  onClick={(e) => { if (!(e.target as HTMLElement).closest("button, a")) toggleRow(o.id); }}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleRow(o.id); } }}
                  className={cn("cursor-pointer border-b border-line last:border-b-0 transition-colors", isSelected ? "bg-info-soft" : "hover:bg-bg/60")}
                >
                  <td className="px-4 py-3 font-mono text-[13px] font-semibold text-ink">{isSelected ? <span className="inline-flex items-center gap-1.5"><Check className="size-3.5 text-info" aria-hidden />{o.id}</span> : o.id}</td>
                  <td className="px-4 py-3"><ToneBadge tone={OPP_TYPE_TONE[o.type]}>{o.type}</ToneBadge></td>
                  <td className="px-4 py-3 text-[13px] font-medium text-ink">{o.description}</td>
                  <td className={cn("px-4 py-3 text-[13px] font-bold tabular-nums", o.estYield === "—" ? "text-muted" : "text-success")}>{o.estYield}</td>
                  <td className="px-4 py-3"><ToneBadge tone={OPP_RISK_TONE[o.risk]}>{o.risk}</ToneBadge></td>
                  <td className="px-4 py-3"><OppSourceCell o={o} /></td>
                  <td className="px-4 py-3">{isExpired ? <ToneBadge tone="danger">Expired Quote</ToneBadge> : <ToneBadge tone={OPP_STATUS_TONE[o.status]}>{o.status}</ToneBadge>}</td>
                  <td className="px-4 py-3 text-right">
                    {isExpired ? (
                      <Button size="sm" variant="secondary" onClick={refreshQuote}>{QUOTE.action}</Button>
                    ) : (
                      <Button size="sm" variant="primary" onClick={() => setIntentOpp(o)}>Create Intent</Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableWrapper>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button size="sm" variant="secondary" aria-expanded={filterOpen} onClick={() => setFilterOpen((v) => !v)}>Filter by Type</Button>
        <Button size="sm" variant="secondary" aria-pressed={sortByYield} onClick={() => setSortByYield((v) => !v)}>Sort by Yield</Button>
        <Button size="sm" variant="secondary" disabled={selected.size === 0} onClick={dismissSelected}>Dismiss Selected</Button>
      </div>
      {filterOpen && (
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" role="radio" aria-checked={typeFilter === "ALL"} className={cn("rounded-lg border px-3 py-1.5 text-xs font-semibold", typeFilter === "ALL" ? "border-primary bg-primary text-on-primary" : "border-line bg-surface text-muted")} onClick={() => { setTypeFilter("ALL"); setFilterOpen(false); }}>All types</button>
          {OPP_TYPES.map((t) => (
            <button key={t} type="button" role="radio" aria-checked={typeFilter === t} className={cn("rounded-lg border px-3 py-1.5 text-xs font-semibold", typeFilter === t ? "border-primary bg-primary text-on-primary" : "border-line bg-surface text-muted")} onClick={() => { setTypeFilter(t); setFilterOpen(false); }}>{t}</button>
          ))}
        </div>
      )}
      <Modal open={!!intentOpp} onOpenChange={(v) => !v && setIntentOpp(null)} title={intentOpp ? `Create Intent — ${intentOpp.id}` : ""} desc="Stage a treasury intent for policy approval.">
        {intentOpp && (
          <>
            <dl className="grid grid-cols-2 gap-y-3 text-sm">
              {([["ID", intentOpp.id], ["Type", intentOpp.type], ["Description", intentOpp.description], ["Est. Yield", intentOpp.estYield], ["Risk", intentOpp.risk], ["Source", intentOpp.source]] as [string, string][]).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3"><dt className="text-muted">{k}</dt><dd className="text-right font-semibold text-ink">{v}</dd></div>
              ))}
            </dl>
            <div className="mt-4 rounded border border-line bg-bg p-3 text-xs text-muted">
              Intent creation is a static fixture. A staged intent would enter policy evaluation and approval routing — that flow requires the intent/approval API (backend dependency). Nothing was executed.
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setIntentOpp(null)}>Cancel</Button>
              <Button size="sm" onClick={stageIntent}>Stage Intent (Fixture)</Button>
            </div>
          </>
        )}
      </Modal>
      <TreasuryBanner label="TRS-01: Opportunity Queue" />
    </div>
  );
}

/* ─── TRS-02 Treasury Intent Detail ───
   Canonical source: specs/06-treasury-operations/mocks/treasury-home-wireframes.html#TRS-02
   ("happy" state only — exception/permission/degraded/empty/readonly state
   tabs exist in the wireframe but are not implemented here; see the TRS-02
   audit section for the reasoning). The canonical happy-state frame names
   its own example intent explicitly ("Intent ID: TRS-INT-0051"), so this
   screen renders TRS_INTENT_0051 verbatim — no intent data was invented or
   duplicated. */
const TRS02_STEP_TONE = (step: TrsIntentTimelineStep): TreasuryTone =>
  step.done ? "success" : step.active ? "info" : "neutral";

const TRS02_DOT_TONE: Record<TreasuryTone, string> = {
  success: "bg-success", warning: "bg-warning", danger: "bg-danger",
  info: "bg-info", pending: "bg-pending", neutral: "bg-disabled",
};

export function IntentDetail() {
  const I = TRS_INTENT_0051;
  const deferTo = (frame: string, label: string) =>
    toast({
      variant: "info",
      title: `${label} — not implemented`,
      description: `${frame} is deferred to a later treasury task, or references an id that does not exist in the real registry. No navigation was made; nothing was created or executed.`,
    });

  const amountLabel = I.amount.split(" (")[0];
  const routeProvider = I.route.split("→ ")[1]?.split(" (")[0] ?? I.route;

  return (
    <div>
      <BackLink href="/treasury/opportunities">Back to Opportunities</BackLink>
      <PageHeader title="Treasury Intent Detail" subtitle="Full lifecycle view for a staking intent: state machine, approval route, policy evaluation, execution timeline." />

      <Panel title={`Intent: ${I.type} ${amountLabel} via ${routeProvider}`} bodyClassName="px-5 py-4">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
          <dl className="grid grid-cols-[140px_1fr] gap-y-3 text-sm">
            <dt className="text-muted">Intent ID</dt><dd className="font-mono font-semibold text-ink">{I.id}</dd>
            <dt className="text-muted">Type</dt><dd><ToneBadge tone="success">{I.type}</ToneBadge></dd>
            <dt className="text-muted">Amount</dt><dd className="font-semibold text-ink">{I.amount}</dd>
            <dt className="text-muted">Source Wallet</dt><dd><Link to={`/wallets/${I.sourceWallet}`} className="font-mono font-semibold text-primary hover:underline">{I.sourceWallet}</Link></dd>
            <dt className="text-muted">Route</dt><dd className="text-ink">{I.route}</dd>
            <dt className="text-muted">Created by</dt><dd className="text-ink">{I.createdBy}</dd>
            <dt className="text-muted">Created</dt><dd className="text-ink">{I.created}</dd>
          </dl>
          <div>
            <div className="mb-2 text-xs font-bold uppercase tracking-wide text-muted">Current Status</div>
            <div className="mb-2 rounded-lg border border-line bg-surface px-4 py-3">
              <div className="mb-1 text-xs text-muted">State</div>
              <PositionStatus tone="warning">{I.stateLabel}</PositionStatus>
            </div>
            <p className="text-sm text-text">
              Awaiting approval from {I.approver}.<br />
              <button type="button" className="font-semibold text-primary hover:underline" onClick={() => deferTo("Approval Record", "View in Approvals")}>View in approvals →</button>
            </p>
          </div>
        </div>
      </Panel>

      <Panel title="State Machine" bodyClassName="px-5 py-4">
        <ol className="space-y-4">
          {I.timeline.map((step) => {
            const tone = TRS02_STEP_TONE(step);
            return (
              <li key={step.state} className="flex gap-3">
                <span className={cn("mt-1 size-2.5 shrink-0 rounded-full", TRS02_DOT_TONE[tone])} aria-hidden />
                <div>
                  <div className="text-sm font-bold text-ink">{step.label}</div>
                  {(step.ts || step.detail) && (
                    <div className="text-xs text-muted">
                      {step.ts}{step.ts && step.detail ? " — " : ""}
                      {step.state === "Pending Policy" && step.detail ? (
                        <>{step.detail} <button type="button" className="font-semibold text-primary hover:underline" onClick={() => deferTo("GOV-STK-01", "Policy Record")}>View policy →</button></>
                      ) : step.detail}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </Panel>

      <div className="flex gap-2">
        <Button variant="destructive" size="sm" onClick={() => deferTo("Intent Cancellation", "Cancel Intent")}>Cancel Intent</Button>
        <Button variant="secondary" size="sm" onClick={() => deferTo("TRS-03: Route Comparison", "Edit Route")}>Edit Route</Button>
      </div>

      <TreasuryBanner label="TRS-02: Treasury Intent Detail" />
    </div>
  );
}

/* ─── STK-00 Staking Positions ─── */
const STK_STATUS_TONE: Record<StkPositionStatus, TreasuryTone> = {
  "Active / Earning": "success",
  "Pending Activation": "warning",
  "Unbonding": "danger",
};

export function StakingPositions() {
  const deferTo = (frame: string, label: string) =>
    toast({
      variant: "info",
      title: `${label} — not implemented`,
      description: `${frame} is deferred to a later treasury task. No navigation was made; nothing was created or executed.`,
    });

  return (
    <div>
      <BackLink href="/treasury">Back to Treasury Home</BackLink>
      <PageHeader title="Staking Positions" subtitle={`Operator: ${STK_OVERVIEW_METRICS.operator} · Last sync: ${STK_OVERVIEW_METRICS.lastSync}`} />
      <div className="mb-6 flex items-start gap-2 rounded-lg border border-danger-border bg-danger-soft p-3 text-sm text-danger">
        <span>Validator for STK-003 (Marinade via Mesh) returned a timeout. Position activation may be delayed.{" "}
          <Link to="/evidence/EVD-001" className="font-semibold text-danger underline">View Evidence</Link>
        </span>
      </div>
      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StatCard value={STK_OVERVIEW_METRICS.totalStakedValue} label="Total Staked Value" tone="success" />
        <StatCard value={STK_OVERVIEW_METRICS.activePositions} label="Active Positions" tone="success" />
        <StatCard value={STK_OVERVIEW_METRICS.pendingActivation} label="Pending Activation" tone="warning" />
        <StatCard value={STK_OVERVIEW_METRICS.unbonding} label="Unbonding" tone="warning" />
        <StatCard value={STK_OVERVIEW_METRICS.totalRewardsAccrued} label="Total Rewards Accrued" tone="success" />
      </div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-bold text-ink">Staking Positions</h2>
        <Link to="/treasury/staking/new" className={buttonVariants({ size: "sm" })}>+ Create Staking Intent</Link>
      </div>
      <TableWrapper>
        <Table className="dense">
          <thead><tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted"><th className="px-4 py-2.5">ID</th><th className="px-4 py-2.5">Asset</th><th className="px-4 py-2.5">Network</th><th className="px-4 py-2.5">Wallet</th><th className="px-4 py-2.5">Validator / Route</th><th className="px-4 py-2.5">Amount</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">APY</th><th className="px-4 py-2.5 text-right">Action</th></tr></thead>
          <tbody>
            {STK_POSITIONS.map((p) => (
              <tr key={p.id} className="border-b border-line last:border-b-0">
                <td className="px-4 py-3 font-mono text-[13px] font-semibold text-ink">
                  {p.id}
                  {p.evidenceId && (
                    <div className="mt-0.5">
                      <Link to={`/evidence/${p.evidenceId}`} className="font-sans text-[11px] font-medium text-primary hover:underline">View evidence →</Link>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-[13px] font-bold text-ink">{p.asset}</td>
                <td className="px-4 py-3 text-[13px] text-muted">{p.network}</td>
                <td className="px-4 py-3">
                  <Link to={`/wallets/${p.walletId}`} className="font-mono text-[13px] font-semibold text-primary hover:underline">{p.walletId}</Link>
                </td>
                <td className="px-4 py-3 text-[13px] text-ink">{p.validatorRoute}</td>
                <td className="px-4 py-3 font-mono text-[13px] font-semibold text-ink">{p.amount}</td>
                <td className="px-4 py-3"><PositionStatus tone={STK_STATUS_TONE[p.status]}>{p.status}</PositionStatus></td>
                <td className={cn("px-4 py-3 text-[13px] font-bold tabular-nums", p.apy === "—" ? "text-muted" : "text-success")}>{p.apy}</td>
                <td className="px-4 py-3 text-right">
                  {p.status === "Unbonding" ? (
                    <Link to="/treasury/staking/unstake" className={buttonVariants({ size: "sm", variant: "secondary" })}>Unstake</Link>
                  ) : p.validatorRoute.startsWith("Lido") ? (
                    <Link to="/treasury/staking/validator" className={buttonVariants({ size: "sm", variant: "secondary" })}>Details</Link>
                  ) : (
                    <Button size="sm" variant="secondary" onClick={() => deferTo("STK-02", "Validator Detail")}>Details</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>
      <TreasuryBanner label="STK-00: Staking Positions" />
    </div>
  );
}

/* ─── STK-01 Create Staking Intent ───
   Canonical source: specs/06-treasury-operations/mocks/staking-wireframes.html#STK-01
   ("happy" state only — exception/permission/degraded/empty/readonly state
   tabs exist in the wireframe but are not implemented here; see the STK-01
   audit section for the reasoning). Form defaults, option lists, and the
   Estimated Summary figures are STK_CREATE_INTENT_FORM verbatim — no APY,
   commission, or reward figure is computed. */
export function CreateStakingIntent() {
  const canCreate = usePermission("treasury:create-intent");
  const F = STK_CREATE_INTENT_FORM;
  const [asset, setAsset] = React.useState(F.assetOptions[0]);
  const [walletId, setWalletId] = React.useState(F.sourceWalletOptions[0].wallet);
  const [route, setRoute] = React.useState(F.validatorRouteOptions[0]);
  const [amount, setAmount] = React.useState(F.amount);
  const [submitted, setSubmitted] = React.useState(false);

  const deferTo = (frame: string, label: string) =>
    toast({
      variant: "info",
      title: `${label} — not implemented`,
      description: `${frame} is deferred to a later treasury task. No navigation was made; nothing was created or executed.`,
    });

  const selectedWallet = F.sourceWalletOptions.find((w) => w.wallet === walletId) ?? F.sourceWalletOptions[0];
  const assetSymbol = asset.split(" — ")[0];
  const amountNum = parseFloat(amount.replace(/,/g, ""));
  const isValidAmount = !isNaN(amountNum) && amountNum > 0;
  const isCanonicalDefault = asset === F.assetOptions[0] && walletId === F.sourceWalletOptions[0].wallet && route === F.validatorRouteOptions[0] && amount === F.amount;

  if (submitted) {
    return (
      <div>
        <BackLink href="/treasury/staking">Back to Staking Positions</BackLink>
        <PageHeader title="Staking Intent Submitted" subtitle="Pending policy approval." />
        <div className="max-w-xl rounded-lg border border-line bg-surface p-6">
          <div className="mb-4 flex items-center gap-2 text-success">
            <Check className="size-5" aria-hidden />
            <span className="font-bold">Submitted for approval</span>
          </div>
          <dl className="grid grid-cols-2 gap-y-3 text-sm">
            <div className="flex justify-between"><dt className="text-muted">Asset</dt><dd className="font-semibold text-ink">{assetSymbol}</dd></div>
            <div className="flex justify-between"><dt className="text-muted">Amount</dt><dd className="font-semibold text-ink">{amount} {assetSymbol}</dd></div>
            <div className="flex justify-between"><dt className="text-muted">Source Wallet</dt><dd className="font-semibold text-ink"><Link to={`/wallets/${walletId}`} className="text-primary hover:underline">{walletId}</Link></dd></div>
            <div className="flex justify-between"><dt className="text-muted">Validator / Provider Route</dt><dd className="font-semibold text-ink">{route}</dd></div>
          </dl>
          <div className="mt-4 rounded border border-info-border bg-info-soft p-3 text-xs text-info">
            {F.policyNote} Policy evaluation and approval routing require the intent/approval API (backend dependency) — nothing was executed or approved.
          </div>
          <div className="mt-5">
            <Link to="/treasury/staking" className={buttonVariants({ size: "sm" })}>Back to Staking Positions</Link>
          </div>
        </div>
        <TreasuryBanner label="STK-01: Create Staking Intent (Submitted)" />
      </div>
    );
  }

  return (
    <div>
      <BackLink href="/treasury/staking">Back to Staking Positions</BackLink>
      <PageHeader title="Create Staking Intent" subtitle={`Operator: ${STK_OVERVIEW_METRICS.operator}`} />
      <div className="grid max-w-4xl gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="rounded-lg border border-line bg-surface p-6">
          <div className="space-y-5">
            <div>
              <label className="mb-1 block text-sm font-semibold text-ink" htmlFor="stk-asset">Asset</label>
              <select id="stk-asset" value={asset} onChange={(e) => setAsset(e.target.value)} className="w-full rounded border border-line-strong px-3 py-2 text-sm text-ink outline-none">
                {F.assetOptions.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-ink" htmlFor="stk-network">Network</label>
              <select id="stk-network" value={F.network} disabled className="w-full rounded border border-line-strong bg-bg px-3 py-2 text-sm text-muted outline-none">
                <option>{F.network}</option>
              </select>
              <div className="mt-1 text-xs text-muted">{F.networkHint}</div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-ink" htmlFor="stk-wallet">Source Wallet</label>
              <select id="stk-wallet" value={walletId} onChange={(e) => setWalletId(e.target.value)} className="w-full rounded border border-line-strong px-3 py-2 text-sm text-ink outline-none">
                {F.sourceWalletOptions.map((w) => <option key={w.wallet} value={w.wallet}>{w.wallet} — {w.label} ({w.available})</option>)}
              </select>
              <div className="mt-1 text-xs">
                <Link to={`/wallets/${walletId}`} className="font-semibold text-primary hover:underline">View wallet details</Link>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-ink" htmlFor="stk-route">Validator / Provider Route</label>
              <select id="stk-route" value={route} onChange={(e) => setRoute(e.target.value)} className="w-full rounded border border-line-strong px-3 py-2 text-sm text-ink outline-none">
                {F.validatorRouteOptions.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <div className="mt-1 text-xs">
                {route.startsWith("Lido") ? (
                  <Link to="/treasury/staking/validator" className="font-semibold text-primary hover:underline">View validator details</Link>
                ) : (
                  <button type="button" className="font-semibold text-primary hover:underline" onClick={() => deferTo("STK-02", "Validator Detail")}>View validator details</button>
                )}
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-ink" htmlFor="stk-amount">Amount</label>
              <input id="stk-amount" type="text" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full rounded border border-line-strong px-3 py-2 text-sm text-ink outline-none" />
              <div className="mt-1 text-xs text-muted">Available balance: {selectedWallet.available}</div>
            </div>
          </div>
        </div>

        <div>
          <div className="rounded-lg border border-line bg-bg p-4">
            <div className="mb-3 text-sm font-bold text-ink">Estimated Summary</div>
            <dl className="space-y-2 text-xs">
              <div className="flex justify-between"><dt className="text-muted">Asset</dt><dd className="font-semibold text-ink">{assetSymbol}</dd></div>
              <div className="flex justify-between"><dt className="text-muted">Amount</dt><dd className="font-semibold text-ink">{amount || "—"} {assetSymbol}</dd></div>
              <div className="flex justify-between"><dt className="text-muted">Estimated APY</dt><dd className="font-semibold text-ink">{isCanonicalDefault ? F.estimatedSummary.estimatedApy : "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-muted">Provider</dt><dd className="font-semibold text-ink">{route}</dd></div>
              <div className="flex justify-between"><dt className="text-muted">Commission</dt><dd className="font-semibold text-ink">{isCanonicalDefault ? F.estimatedSummary.commission : "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-muted">Activation Time</dt><dd className="font-semibold text-ink">{isCanonicalDefault ? F.estimatedSummary.activationTime : "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-muted">Unbonding Period</dt><dd className="font-semibold text-ink">{isCanonicalDefault ? F.estimatedSummary.unbondingPeriod : "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-muted">Est. Annual Reward</dt><dd className="font-semibold text-ink">{isCanonicalDefault ? F.estimatedSummary.estAnnualReward : "—"}</dd></div>
            </dl>
            {!isCanonicalDefault && (
              <div className="mt-3 text-[11px] leading-relaxed text-muted">Live figures for this selection require provider data (not available in this preview).</div>
            )}
          </div>
          <div className="mt-3 rounded-lg border border-info-border bg-info-soft p-3 text-xs text-info">
            {F.policyNote}{" "}
            <button type="button" className="font-bold underline" onClick={() => deferTo("APR-01", "Approval Policy")}>View approval policy</button>
          </div>
        </div>
      </div>

      <div className="mt-6 flex max-w-4xl items-center justify-end gap-3">
        {!isValidAmount && <span className="text-xs text-muted">Enter an amount greater than 0 to continue.</span>}
        <Link to="/treasury/staking" className={buttonVariants({ variant: "secondary", size: "sm" })}>Cancel</Link>
        <Button
          size="sm"
          disabled={!isValidAmount || !canCreate}
          onClick={() => {
            if (!canCreate) return;
            setSubmitted(true);
            toast({
              variant: "success",
              title: "Staking intent submitted for approval",
              description: localOnly(`${amount} ${assetSymbol} via ${route} — pending approval per policy GOV-STAK-01.`),
            });
          }}
        >
          Submit Intent for Approval
        </Button>
      </div>
      <TreasuryBanner label="STK-01: Create Staking Intent" />
    </div>
  );
}

/* ─── STK-02 Validator Detail ───
   Canonical source: specs/06-treasury-operations/mocks/staking-wireframes.html#STK-02
   ("happy" state only — see the STK-02 audit section for reasoning). Fixed,
   non-parameterized route: STK_VALIDATOR_LIDO is the only validator with any
   canonical detail data, so a dynamic [id] route would imply resolving ids
   that don't exist in the fixture. Every figure below is rendered verbatim
   from STK_VALIDATOR_LIDO — nothing computed, nothing simulated. */
const STK02_CAPABILITY_TONE: Record<string, TreasuryTone> = {
  "Liquid Staking": "info",
  "MEV Boost": "success",
  "Governance Voting": "neutral",
  "Auto-compound": "success",
};

export function ValidatorDetail() {
  const v = STK_VALIDATOR_LIDO;
  return (
    <div>
      <BackLink href="/treasury/staking">Back to Positions</BackLink>
      <PageHeader title={`${v.name} — Validator Detail`} subtitle={v.protocol} actions={<PositionStatus tone="success">Active</PositionStatus>} />

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="rounded-lg border border-line bg-surface p-5">
          <div className="mb-3 text-sm font-bold text-ink">Provider Info</div>
          <dl className="divide-y divide-line text-sm">
            <div className="flex items-center justify-between gap-3 py-2"><dt className="text-muted">Provider</dt><dd className="font-semibold text-ink">{v.name}</dd></div>
            <div className="flex items-center justify-between gap-3 py-2"><dt className="text-muted">Protocol</dt><dd className="font-semibold text-ink">{v.protocol}</dd></div>
            <div className="flex items-center justify-between gap-3 py-2"><dt className="text-muted">Routes</dt><dd className="font-semibold text-ink">{v.routes.join(", ")}</dd></div>
            <div className="flex items-center justify-between gap-3 py-2"><dt className="text-muted">Supported Networks</dt><dd className="font-semibold text-ink">{v.networks.join(", ")}</dd></div>
            <div className="flex items-center justify-between gap-3 py-2"><dt className="text-muted">Commission</dt><dd className="font-semibold text-ink">{v.commission}</dd></div>
            <div className="flex items-center justify-between gap-3 py-2"><dt className="text-muted">Current APY</dt><dd className="font-semibold text-success">{v.currentApy}</dd></div>
            <div className="flex items-center justify-between gap-3 py-2"><dt className="text-muted">Total Delegated</dt><dd className="font-semibold text-ink">{v.totalDelegated}</dd></div>
            <div className="flex items-center justify-between gap-3 py-2"><dt className="text-muted">Your Delegation</dt><dd className="font-semibold text-ink">{v.yourDelegation}</dd></div>
          </dl>
        </div>

        <div className="rounded-lg border border-line bg-surface p-5">
          <div className="mb-3 text-sm font-bold text-ink">Performance &amp; Risk</div>
          <dl className="divide-y divide-line text-sm">
            <div className="flex items-center justify-between gap-3 py-2"><dt className="text-muted">Uptime (30d)</dt><dd className="font-semibold text-ink">{v.uptime30d}</dd></div>
            <div className="flex items-center justify-between gap-3 py-2"><dt className="text-muted">Uptime (90d)</dt><dd className="font-semibold text-ink">{v.uptime90d}</dd></div>
            <div className="flex items-center justify-between gap-3 py-2"><dt className="text-muted">Slashing History</dt><dd><ToneBadge tone="success">{v.slashingHistory}</ToneBadge></dd></div>
            <div className="flex items-center justify-between gap-3 py-2"><dt className="text-muted">Last Incident</dt><dd className="font-semibold text-ink">{v.lastIncident}</dd></div>
            <div className="flex items-center justify-between gap-3 py-2"><dt className="text-muted">Audit Status</dt><dd><ToneBadge tone="success">{v.auditStatus}</ToneBadge></dd></div>
            <div className="flex items-center justify-between gap-3 py-2"><dt className="text-muted">Insurance</dt><dd><ToneBadge tone="info">{v.insurance}</ToneBadge></dd></div>
          </dl>
          <div className="mt-4">
            <div className="mb-2 text-sm font-bold text-ink">Capability Badges</div>
            <div className="flex flex-wrap gap-1.5">
              {v.capabilityBadges.map((b) => (
                <ToneBadge key={b} tone={STK02_CAPABILITY_TONE[b] ?? "neutral"}>{b}</ToneBadge>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-5 rounded-lg border border-line bg-surface p-5">
        <div className="mb-3 text-sm font-bold text-ink">{v.chart.title}</div>
        <div className="rounded border border-dashed border-line bg-bg px-8 py-10 text-center text-[13px] text-muted">
          Wireframe placeholder: line chart showing daily APY over 30 days, range {v.chart.range}.
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Link to="/treasury/staking/new" className={buttonVariants({ size: "sm" })}>Stake with Lido</Link>
        <Link to="/treasury/staking" className={buttonVariants({ variant: "secondary", size: "sm" })}>Back to Positions</Link>
        <Link to="/evidence/EVD-006" className="text-sm font-semibold text-primary hover:underline">View evidence log</Link>
      </div>
      <TreasuryBanner label="STK-02: Validator Detail" />
    </div>
  );
}

/* ─── STK-03 Rewards & Accounting ───
   Canonical source: specs/06-treasury-operations/mocks/staking-wireframes.html#STK-03
   ("happy" state only — see the STK-03 audit section for reasoning). Every
   figure is rendered verbatim from STK_REWARDS — nothing computed, nothing
   simulated. No canonical inbound link exists from STK-00/01/02 to this
   screen (verified fresh against the source — the "Total Rewards Accrued"
   metric card on STK-00 is plain, not a crosslink), so none was added. */
const STK03_RECON_TONE: Record<string, TreasuryTone> = {
  Reconciled: "success",
  Pending: "neutral",
  "Pending Recon": "warning",
};
const STK03_EXPORT_TONE: Record<string, TreasuryTone> = {
  Marked: "info",
  "—": "neutral",
};

export function StakingRewards() {
  const R = STK_REWARDS;
  return (
    <div>
      <BackLink href="/treasury/staking">Back to Staking Positions</BackLink>
      <PageHeader title="Rewards & Accounting" subtitle={`Viewed by: ${R.viewedBy} · Data as of epoch ${R.epoch}`} />

      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard value={R.totalAccrued} label="Total Accrued" tone="success" />
        <StatCard value={R.claimed} label="Claimed" tone="success" />
        <StatCard value={R.unclaimed} label="Unclaimed" tone="warning" />
        <StatCard value={R.reconciled} label="Reconciled" tone="success" />
      </div>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-bold text-ink">Rewards by Position</h2>
        <div className="flex items-center gap-3">
          <Button size="sm" variant="ghost">Export CSV</Button>
          <Link to="/reconciliation" className="text-sm font-semibold text-primary hover:underline">Reconciliation</Link>
        </div>
      </div>

      <TableWrapper>
        <Table className="dense">
          <thead>
            <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
              <th className="px-4 py-2.5">Position</th>
              <th className="px-4 py-2.5">Asset</th>
              <th className="px-4 py-2.5">Accrued</th>
              <th className="px-4 py-2.5">Claimed</th>
              <th className="px-4 py-2.5">Unclaimed</th>
              <th className="px-4 py-2.5">Recon Status</th>
              <th className="px-4 py-2.5">Export</th>
            </tr>
          </thead>
          <tbody>
            {R.rows.map((r) => (
              <tr key={r.position} className="border-b border-line last:border-b-0">
                <td className="px-4 py-3 font-mono text-[13px] font-semibold text-ink">{r.position}</td>
                <td className="px-4 py-3 text-[13px] font-bold text-ink">{r.asset}</td>
                <td className="px-4 py-3 font-mono text-[13px] text-ink">{r.accrued}</td>
                <td className="px-4 py-3 font-mono text-[13px] text-ink">{r.claimed}</td>
                <td className="px-4 py-3 font-mono text-[13px] text-ink">{r.unclaimed}</td>
                <td className="px-4 py-3"><ToneBadge tone={STK03_RECON_TONE[r.reconStatus] ?? "neutral"}>{r.reconStatus}</ToneBadge></td>
                <td className="px-4 py-3"><ToneBadge tone={STK03_EXPORT_TONE[r.exportStatus] ?? "neutral"}>{r.exportStatus}</ToneBadge></td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>
      <TreasuryBanner label="STK-03: Rewards & Accounting" />
    </div>
  );
}

/* ─── STK-04 Unstake / Withdraw ───
   Canonical source: specs/06-treasury-operations/mocks/staking-wireframes.html#STK-04
   ("happy" state only — see the STK-04 audit section for reasoning). This is
   a READ-ONLY progress view of an already-requested unstake (STK-004 is
   already "Unbonding" in STK_POSITIONS) — not an initiation form. The
   canonical "Withdraw to Wallet" button is itself DISABLED in the happy
   state ("Available after cooldown"), so no state mutation, submission, or
   confirmation flow was invented here; rendering it disabled is the honest,
   literal implementation of what the source specifies. */
const STK04_DOT_TONE: Record<TreasuryTone, string> = {
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
  info: "bg-info",
  pending: "bg-pending",
  neutral: "bg-disabled",
};

export function UnstakeWithdraw() {
  const U = STK_UNSTAKE_004;
  const deferTo = (frame: string, label: string) =>
    toast({
      variant: "info",
      title: `${label} — not implemented`,
      description: `${frame} is deferred to a later treasury task, or references an id that does not exist in the real registry. No navigation was made; nothing was created or executed.`,
    });

  const steps: { title: string; tone: TreasuryTone; meta: React.ReactNode }[] = [
    { title: "Unstake Requested", tone: "success", meta: `${U.requestedAt} — ${U.requestedBy}` },
    {
      title: "Approval Granted",
      tone: "success",
      meta: (
        <>
          {U.approvalGrantedAt} —{" "}
          <button type="button" className="font-semibold text-primary hover:underline" onClick={() => deferTo("Approval Record", U.approvalRef)}>
            {U.approvalRef}
          </button>
        </>
      ),
    },
    {
      title: `Cooldown Period (${U.cooldownDays} days)`,
      tone: "info",
      meta: `In progress — ${U.elapsedDays} of ${U.cooldownDays} days elapsed. Estimated completion: ${U.estimatedCompletion}`,
    },
    { title: "Withdrawal Available", tone: "neutral", meta: `Expected: ${U.withdrawalAvailable}` },
    { title: "Withdrawn to Wallet", tone: "neutral", meta: "Pending — Manual confirmation required after cooldown" },
  ];

  return (
    <div>
      <BackLink href="/treasury/staking">Back to Staking Positions</BackLink>
      <PageHeader title={`Unstake — ${U.position}`} subtitle={`${U.asset} · ${U.network}`} actions={<PositionStatus tone="danger">Unbonding</PositionStatus>} />

      <div className="max-w-2xl rounded-lg border border-line bg-surface p-5">
        <dl className="divide-y divide-line text-sm">
          <div className="flex items-center justify-between gap-3 py-2"><dt className="text-muted">Position</dt><dd className="font-mono font-semibold text-ink">{U.position}</dd></div>
          <div className="flex items-center justify-between gap-3 py-2"><dt className="text-muted">Asset</dt><dd className="font-semibold text-ink">{U.asset}</dd></div>
          <div className="flex items-center justify-between gap-3 py-2"><dt className="text-muted">Network</dt><dd className="font-semibold text-ink">{U.network}</dd></div>
          <div className="flex items-center justify-between gap-3 py-2"><dt className="text-muted">Wallet</dt><dd><Link to={`/wallets/${U.wallet}`} className="font-mono font-semibold text-primary hover:underline">{U.wallet}</Link></dd></div>
          <div className="flex items-center justify-between gap-3 py-2"><dt className="text-muted">Provider</dt><dd className="font-semibold text-ink">{U.provider}</dd></div>
          <div className="flex items-center justify-between gap-3 py-2"><dt className="text-muted">Requested By</dt><dd className="font-semibold text-ink">{U.requestedBy}</dd></div>
          <div className="flex items-center justify-between gap-3 py-2"><dt className="text-muted">Requested At</dt><dd className="font-semibold text-ink">{U.requestedAt}</dd></div>
        </dl>
      </div>

      <div className="mt-5 max-w-2xl rounded-lg border border-line bg-surface p-5">
        <div className="mb-4 text-sm font-bold text-ink">Withdrawal Timeline</div>
        <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-disabled-soft">
          <div className="h-full rounded-full bg-warning" style={{ width: `${U.progressPct}%` }} />
        </div>
        <div className="mb-5 flex justify-between text-[11px] text-muted">
          <span>{U.dayZeroLabel}</span>
          <span>Day {U.elapsedDays} of {U.cooldownDays} — {U.progressPct}% complete</span>
          <span>{U.daySevenLabel}</span>
        </div>

        <ol className="relative">
          {steps.map((s, i) => (
            <li key={s.title} className="relative flex gap-3 pb-5 last:pb-0">
              {i < steps.length - 1 && <span aria-hidden className="absolute left-[5px] top-4 h-full w-px bg-line" />}
              <span aria-hidden className={cn("relative z-10 mt-1.5 size-[11px] shrink-0 rounded-full ring-2 ring-surface", STK04_DOT_TONE[s.tone])} />
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-bold text-ink">{s.title}</div>
                <div className="mt-0.5 text-xs leading-relaxed text-muted">{s.meta}</div>
              </div>
            </li>
          ))}
        </ol>
      </div>

      <InfoBanner>
        Once the cooldown completes, a withdrawal transaction must be submitted and approved.{" "}
        <button type="button" className="font-bold underline" onClick={() => deferTo("Evidence Record", "View unstake evidence")}>
          View unstake evidence
        </button>
      </InfoBanner>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Button variant="destructive" size="sm" disabled title="Available after cooldown">Withdraw to Wallet</Button>
        <Link to="/treasury/staking" className={buttonVariants({ variant: "secondary", size: "sm" })}>Back to Positions</Link>
      </div>
      <TreasuryBanner label="STK-04: Unstake / Withdraw" />
    </div>
  );
}

/* ─── LND-00 Lending Overview ───
   Canonical source: specs/06-treasury-operations/mocks/lending-wireframes.html#LND-00
   ("happy" state — this wireframe file's own script explicitly comments
   its state-tab buttons as "visual toggle only": no Exception/Permission/
   Degraded/Empty/Read-only content was ever authored for ANY LND frame in
   this source file (unlike treasury-home-wireframes.html, which fully
   authored every state). The 5 non-happy states are therefore NOT IN
   SOURCE for this screen, not merely deferred — see the LND-00 audit
   section. Reuses LND_POSITIONS/LND_OVERVIEW_METRICS (Task-1 foundation,
   wired up here for the first time). This fully replaces the prior
   unaudited LendingOverview() prototype — none of its panels (a bar
   chart, "Available Protocols", "OTC Credit Facility", per-row
   Withdraw/Renew modals with a fake "Submit for Approval" flow) exist in
   the canonical LND-00 happy state, so none were preserved. */
const LND_DISPOSITION_LEGEND: { label: string; tone: TreasuryTone }[] = [
  { label: "supplied", tone: "success" },
  { label: "borrowed", tone: "info" },
  { label: "repaid", tone: "neutral" },
  { label: "closed", tone: "neutral" },
  { label: "margin-warning", tone: "warning" },
  { label: "liquidated", tone: "danger" },
  { label: "failed-terminal", tone: "danger" },
  { label: "rejected", tone: "neutral" },
  { label: "cancelled", tone: "neutral" },
  { label: "reconciled", tone: "success" },
];

/* Only LND-001 and LND-002 render as cross-links to LND-03 in the
   canonical source (plain onclick handlers, no href) — LND-003/004/005
   are plain text IDs there, so no link was invented for them. LND-002 is a
   REAL link (LND-03/LND_DETAIL_002 is now implemented, Lending Completion
   phase); LND-001 stays an honest deferral because no detail-page data
   exists for it — LND-03 only has canonical detail content for LND-002,
   so linking LND-001 to that same screen would show the wrong position's
   data, which this task's own instructions explicitly warn against. */
const LND00_CROSSLINK_IDS = new Set(["LND-001", "LND-002"]);
const LND00_REAL_DETAIL_IDS = new Set(["LND-002"]);

const lndStatusTone = (p: LndPosition): TreasuryTone => {
  if (p.status === "Margin Warning") return "warning";
  if (p.status === "Matured / Closed") return "neutral";
  return p.type === "Supply" ? "success" : "info";
};
const lndStatusText = (p: LndPosition) => (p.status === "Active" && p.apy !== "—" ? `Active (${p.apy} APY)` : p.status);

export function LendingOverview() {
  const M = LND_OVERVIEW_METRICS;
  const deferTo = (frame: string, label: string) =>
    toast({
      variant: "info",
      title: `${label} — not implemented`,
      description: `${frame} is deferred to a later treasury task, or references an id that does not exist in the real registry. No navigation was made; nothing was created or executed.`,
    });

  return (
    <div>
      <BackLink href="/treasury">Back to Treasury Home</BackLink>
      <PageHeader title="Lending" />

      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard value={M.totalLent} label="Total Supplied" hint={M.lendingCount} />
        <StatCard value={M.totalBorrowed} label="Total Borrowed" hint={M.borrowedCount} />
        <StatCard value={M.avgHealthFactor} label="Avg Health Factor" tone="success" hint="Across borrow positions" />
        <StatCard value={M.marginWarnings} label="Margin Warnings" tone="warning" hint={M.marginWarningDetail} />
      </div>

      <InfoBanner variant="warning">
        <strong>Margin Warning:</strong> {M.marginWarning}{" "}
        <button type="button" className="font-semibold underline" onClick={() => deferTo("MON-05", "Monitoring Alert")}>View in Monitoring →</button>
      </InfoBanner>

      <Panel title="Lending Positions" bodyClassName="px-5 py-4">
        <div className="mb-4 flex flex-wrap gap-2">
          {LND_DISPOSITION_LEGEND.map((d) => (
            <ToneBadge key={d.label} tone={d.tone}>{d.label}</ToneBadge>
          ))}
        </div>
        <TableWrapper className="rounded-none border-0">
          <Table className="dense">
            <thead><tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted"><th className="px-4 py-2.5">ID</th><th className="px-4 py-2.5">Type</th><th className="px-4 py-2.5">Asset</th><th className="px-4 py-2.5">Amount</th><th className="px-4 py-2.5">Venue / Route</th><th className="px-4 py-2.5">Collateral</th><th className="px-4 py-2.5">Health Factor</th><th className="px-4 py-2.5">Status</th></tr></thead>
            <tbody>
              {LND_POSITIONS.map((p) => (
                <tr key={p.id} className={cn("border-b border-line last:border-b-0", p.status === "Margin Warning" && "bg-warning-soft")}>
                  <td className="px-4 py-3 font-mono text-[13px] font-semibold text-ink">
                    {LND00_REAL_DETAIL_IDS.has(p.id) ? (
                      <Link to="/treasury/lending/position" className="font-mono font-semibold text-primary hover:underline">{p.id}</Link>
                    ) : LND00_CROSSLINK_IDS.has(p.id) ? (
                      <button type="button" className="font-mono font-semibold text-primary hover:underline" onClick={() => deferTo("LND-03", "Position Detail")}>{p.id}</button>
                    ) : p.id}
                  </td>
                  <td className="px-4 py-3 text-[13px] text-ink">{p.type}</td>
                  <td className="px-4 py-3 text-[13px] font-semibold text-ink">{p.asset}</td>
                  <td className="px-4 py-3 text-[13px] tabular-nums text-text">{p.amount}</td>
                  <td className="px-4 py-3 text-[13px] text-text">{p.venueRoute}</td>
                  <td className="px-4 py-3 text-[13px] tabular-nums text-text">{p.collateral}</td>
                  <td className={cn("px-4 py-3 text-[13px] font-bold tabular-nums", p.healthFactor === "—" ? "text-muted" : p.status === "Margin Warning" ? "text-warning" : "text-success")}>{p.healthFactor}</td>
                  <td className="px-4 py-3"><ToneBadge tone={lndStatusTone(p)}>{lndStatusText(p)}</ToneBadge></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      </Panel>

      <div className="flex flex-wrap items-center gap-2">
        <Link to="/treasury/lending/supply" className={buttonVariants({ size: "sm" })}>+ New Supply</Link>
        <Link to="/treasury/lending/borrow" className={buttonVariants({ size: "sm", variant: "secondary" })}>+ New Borrow</Link>
      </div>

      <TreasuryBanner label="LND-00: Lending Overview" />
    </div>
  );
}

/* ─── LND-01 New Supply (Supply / Lend Intent) ───
   Canonical source: specs/06-treasury-operations/mocks/lending-wireframes.html#LND-01
   ("happy" state only — this wireframe file's state tabs are, by its own
   script comment, "visual toggle only"; no alternate-state content exists
   for any LND frame — same finding as LND-00). Reuses LND_SUPPLY_INTENT
   verbatim for default form values and Policy Route Preview figures; the
   4 dropdown option lists are literal transcriptions added to that record
   in this phase (see its own doc-comment in treasury-data.ts). Submission
   follows the exact honest, client-side-only "submitted" pattern already
   established in STK-01 CreateStakingIntent — no real intent/approval
   backend exists, so nothing is persisted; the confirmation screen says
   so explicitly. */
export function CreateSupplyIntent() {
  const canCreate = usePermission("treasury:create-intent");
  const F = LND_SUPPLY_INTENT;
  const [asset, setAsset] = React.useState(F.asset);
  const [amount, setAmount] = React.useState(F.amount);
  const [venue, setVenue] = React.useState(F.venue);
  const [route, setRoute] = React.useState(F.route);
  const [submitted, setSubmitted] = React.useState(false);

  const deferTo = (frame: string, label: string) =>
    toast({
      variant: "info",
      title: `${label} — not implemented`,
      description: `${frame} is deferred to a later treasury task, or references an id that does not exist in the real registry. No navigation was made; nothing was created or executed.`,
    });

  const amountNum = parseFloat(amount.replace(/,/g, ""));
  const isValidAmount = !isNaN(amountNum) && amountNum > 0;
  const isCanonicalDefault = asset === F.asset && amount === F.amount && venue === F.venue && route === F.route;

  if (submitted) {
    return (
      <div>
        <BackLink href="/treasury/lending">Back to Lending</BackLink>
        <PageHeader title="Supply Intent Submitted" subtitle="Pending policy approval." />
        <div className="max-w-xl rounded-lg border border-line bg-surface p-6">
          <div className="mb-4 flex items-center gap-2 text-success">
            <Check className="size-5" aria-hidden />
            <span className="font-bold">Submitted for approval</span>
          </div>
          <dl className="grid grid-cols-2 gap-y-3 text-sm">
            <div className="flex justify-between"><dt className="text-muted">Asset</dt><dd className="font-semibold text-ink">{asset}</dd></div>
            <div className="flex justify-between"><dt className="text-muted">Amount</dt><dd className="font-semibold text-ink">{amount} {asset}</dd></div>
            <div className="flex justify-between"><dt className="text-muted">Venue</dt><dd className="font-semibold text-ink">{venue}</dd></div>
            <div className="flex justify-between"><dt className="text-muted">Route</dt><dd className="font-semibold text-ink">{route}</dd></div>
          </dl>
          <div className="mt-4 rounded border border-info-border bg-info-soft p-3 text-xs text-info">
            Policy evaluation and approval routing require the intent/approval API (backend dependency) — nothing was executed or approved.
          </div>
          <div className="mt-5">
            <Link to="/treasury/lending" className={buttonVariants({ size: "sm" })}>Back to Lending</Link>
          </div>
        </div>
        <TreasuryBanner label="LND-01: New Supply (Submitted)" />
      </div>
    );
  }

  return (
    <div>
      <BackLink href="/treasury/lending">Back to Lending</BackLink>
      <PageHeader title="Supply / Lend Intent" subtitle={`Initiated by: ${F.initiatedBy} · ${F.initiatedAt}`} />
      <div className="grid max-w-4xl gap-5 lg:grid-cols-[minmax(0,1fr)_280px]">
        <div className="rounded-lg border border-line bg-surface p-6">
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-sm font-semibold text-ink" htmlFor="lnd-asset">Asset</label>
                <select id="lnd-asset" value={asset} onChange={(e) => setAsset(e.target.value)} className="w-full rounded border border-line-strong px-3 py-2 text-sm text-ink outline-none">
                  {F.assetOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-ink" htmlFor="lnd-amount">Amount</label>
                <input id="lnd-amount" type="text" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full rounded border border-line-strong px-3 py-2 text-sm text-ink outline-none" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-sm font-semibold text-ink" htmlFor="lnd-venue">Venue</label>
                <select id="lnd-venue" value={venue} onChange={(e) => setVenue(e.target.value)} className="w-full rounded border border-line-strong px-3 py-2 text-sm text-ink outline-none">
                  {F.venueOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-ink" htmlFor="lnd-route">Route</label>
                <select id="lnd-route" value={route} onChange={(e) => setRoute(e.target.value)} className="w-full rounded border border-line-strong px-3 py-2 text-sm text-ink outline-none">
                  {F.routeOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="mt-5 rounded-lg border border-info-border bg-info-soft p-4">
            <div className="mb-3 text-sm font-bold text-info">Policy Route Preview</div>
            <dl className="grid grid-cols-2 gap-y-3 text-sm">
              <div><dt className="text-xs text-muted">Required Approvals</dt><dd className="font-semibold text-ink">{isCanonicalDefault ? F.policyRoute.requiredApprovals : "—"}</dd></div>
              <div><dt className="text-xs text-muted">Risk Tier</dt><dd>{isCanonicalDefault ? <ToneBadge tone="success">{F.policyRoute.riskTier}</ToneBadge> : <span className="font-semibold text-ink">—</span>}</dd></div>
              <div><dt className="text-xs text-muted">Counterparty Risk</dt><dd className="font-semibold text-ink">{isCanonicalDefault ? F.policyRoute.counterparty : "—"}</dd></div>
              <div><dt className="text-xs text-muted">Expected APY</dt><dd className="font-bold text-success">{isCanonicalDefault ? F.policyRoute.expectedApy : "—"}</dd></div>
            </dl>
          </div>

          <div className="mt-4 rounded border border-line bg-bg p-3 text-xs text-text">
            {isCanonicalDefault ? (
              <>
                Supply will route through <strong>{route}</strong> to <strong>{venue}</strong>. Requires{" "}
                <button type="button" className="font-semibold text-primary hover:underline" onClick={() => deferTo("APR-01", "Approval Policy")}>2 approvals</button>{" "}
                per policy. Source wallet: <span className="font-mono font-semibold text-ink">{F.sourceWalletLabel}</span>.
              </>
            ) : (
              "Live routing and policy figures for this selection require provider data (not available in this preview)."
            )}
          </div>
        </div>

        <div className="rounded-lg border border-line bg-bg p-4 text-xs text-muted">
          Supply intent creation is a static fixture. Submitting stages an intent that would enter policy evaluation and approval routing — that flow requires the intent/approval API (backend dependency). Nothing is executed on submit.
        </div>
      </div>

      <div className="mt-6 flex max-w-4xl items-center justify-end gap-3">
        {!isValidAmount && <span className="text-xs text-muted">Enter an amount greater than 0 to continue.</span>}
        <Link to="/treasury/lending" className={buttonVariants({ variant: "secondary", size: "sm" })}>Cancel</Link>
        <Button variant="secondary" size="sm" onClick={() => deferTo("Draft Persistence", "Save Draft")}>Save Draft</Button>
        <Button
          size="sm"
          disabled={!isValidAmount || !canCreate}
          onClick={() => {
            if (!canCreate) return;
            setSubmitted(true);
            toast({ variant: "success", title: "Supply intent submitted for approval", description: localOnly(`${amount} ${asset} via ${route} to ${venue} — pending policy approval.`) });
          }}
        >
          Submit Supply Intent
        </Button>
      </div>
      <TreasuryBanner label="LND-01: New Supply" />
    </div>
  );
}

/* ─── LND-02 New Borrow (Borrow Intent) ───
   Canonical source: specs/06-treasury-operations/mocks/lending-wireframes.html#LND-02
   ("happy" state only — same state-coverage finding as LND-00/LND-01).
   Reuses LND_BORROW_INTENT verbatim for default form values and Risk
   Parameters figures (initiatedBy/initiatedAt and the 4 dropdown option
   lists were missing from this fixture on re-inspection and were added
   verbatim from the canonical source — see its own doc-comment in
   treasury-data.ts). Submission follows the same honest, client-side-only
   pattern as LND-01/STK-01 — nothing is persisted. */
export function CreateBorrowIntent() {
  const canCreate = usePermission("treasury:create-intent");
  const F = LND_BORROW_INTENT;
  const [borrowAsset, setBorrowAsset] = React.useState(F.borrowAsset);
  const [borrowAmount, setBorrowAmount] = React.useState(F.borrowAmount);
  const [collateralAsset, setCollateralAsset] = React.useState(F.collateralAsset);
  const [collateralAmount, setCollateralAmount] = React.useState(F.collateralAmount);
  const [repaymentTerms, setRepaymentTerms] = React.useState(F.repaymentTerms);
  const [venue, setVenue] = React.useState(F.venue);
  const [submitted, setSubmitted] = React.useState(false);

  const deferTo = (frame: string, label: string) =>
    toast({
      variant: "info",
      title: `${label} — not implemented`,
      description: `${frame} is deferred to a later treasury task, or references an id that does not exist in the real registry. No navigation was made; nothing was created or executed.`,
    });

  const isPositive = (v: string) => { const n = parseFloat(v.replace(/,/g, "")); return !isNaN(n) && n > 0; };
  const formValid = isPositive(borrowAmount) && isPositive(collateralAmount);
  const isCanonicalDefault = borrowAsset === F.borrowAsset && borrowAmount === F.borrowAmount && collateralAsset === F.collateralAsset && collateralAmount === F.collateralAmount && repaymentTerms === F.repaymentTerms && venue === F.venue;

  if (submitted) {
    return (
      <div>
        <BackLink href="/treasury/lending">Back to Lending</BackLink>
        <PageHeader title="Borrow Intent Submitted" subtitle="Pending policy approval." />
        <div className="max-w-xl rounded-lg border border-line bg-surface p-6">
          <div className="mb-4 flex items-center gap-2 text-success">
            <Check className="size-5" aria-hidden />
            <span className="font-bold">Submitted for approval</span>
          </div>
          <dl className="grid grid-cols-2 gap-y-3 text-sm">
            <div className="flex justify-between"><dt className="text-muted">Borrow</dt><dd className="font-semibold text-ink">{borrowAmount} {borrowAsset}</dd></div>
            <div className="flex justify-between"><dt className="text-muted">Collateral</dt><dd className="font-semibold text-ink">{collateralAmount} {collateralAsset}</dd></div>
            <div className="flex justify-between"><dt className="text-muted">Repayment Terms</dt><dd className="font-semibold text-ink">{repaymentTerms}</dd></div>
            <div className="flex justify-between"><dt className="text-muted">Venue</dt><dd className="font-semibold text-ink">{venue}</dd></div>
          </dl>
          <div className="mt-4 rounded border border-info-border bg-info-soft p-3 text-xs text-info">
            Policy evaluation, collateral verification, and approval routing require the intent/approval API (backend dependency) — nothing was executed or approved.
          </div>
          <div className="mt-5">
            <Link to="/treasury/lending" className={buttonVariants({ size: "sm" })}>Back to Lending</Link>
          </div>
        </div>
        <TreasuryBanner label="LND-02: New Borrow (Submitted)" />
      </div>
    );
  }

  return (
    <div>
      <BackLink href="/treasury/lending">Back to Lending</BackLink>
      <PageHeader title="Borrow Intent" subtitle={`Initiated by: ${F.initiatedBy} · ${F.initiatedAt}`} />
      <div className="max-w-4xl rounded-lg border border-line bg-surface p-6">
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-sm font-semibold text-ink" htmlFor="lnd-borrow-asset">Borrow Asset</label>
              <select id="lnd-borrow-asset" value={borrowAsset} onChange={(e) => setBorrowAsset(e.target.value)} className="w-full rounded border border-line-strong px-3 py-2 text-sm text-ink outline-none">
                {F.borrowAssetOptions.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-ink" htmlFor="lnd-borrow-amount">Borrow Amount</label>
              <input id="lnd-borrow-amount" type="text" inputMode="decimal" value={borrowAmount} onChange={(e) => setBorrowAmount(e.target.value)} className="w-full rounded border border-line-strong px-3 py-2 text-sm text-ink outline-none" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-sm font-semibold text-ink" htmlFor="lnd-collateral-asset">Collateral Asset</label>
              <select id="lnd-collateral-asset" value={collateralAsset} onChange={(e) => setCollateralAsset(e.target.value)} className="w-full rounded border border-line-strong px-3 py-2 text-sm text-ink outline-none">
                {F.collateralAssetOptions.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-ink" htmlFor="lnd-collateral-amount">Collateral Amount</label>
              <input id="lnd-collateral-amount" type="text" inputMode="decimal" value={collateralAmount} onChange={(e) => setCollateralAmount(e.target.value)} className="w-full rounded border border-line-strong px-3 py-2 text-sm text-ink outline-none" />
            </div>
          </div>
        </div>

        <div className="mt-5 rounded-lg border border-line bg-bg p-4">
          <div className="mb-3 text-sm font-bold text-ink">Risk Parameters</div>
          <dl className="grid grid-cols-2 gap-y-3 text-sm lg:grid-cols-4">
            <div><dt className="text-xs text-muted">Loan-to-Value (LTV)</dt><dd className="font-semibold text-ink">{isCanonicalDefault ? F.riskParams.ltv : "—"}</dd></div>
            <div><dt className="text-xs text-muted">Liquidation Threshold</dt><dd className="font-bold text-danger">{isCanonicalDefault ? F.riskParams.liquidationThreshold : "—"}</dd></div>
            <div><dt className="text-xs text-muted">Initial Health Factor</dt><dd className="font-bold text-success">{isCanonicalDefault ? F.riskParams.initialHealthFactor : "—"}</dd></div>
            <div><dt className="text-xs text-muted">Liquidation Penalty</dt><dd className="font-semibold text-ink">{isCanonicalDefault ? F.riskParams.liquidationPenalty : "—"}</dd></div>
          </dl>
          {!isCanonicalDefault && <div className="mt-3 text-[11px] leading-relaxed text-muted">Live risk figures for this selection require provider data (not available in this preview).</div>}
        </div>

        <div className="mt-5 grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1 block text-sm font-semibold text-ink" htmlFor="lnd-repayment">Repayment Terms</label>
            <select id="lnd-repayment" value={repaymentTerms} onChange={(e) => setRepaymentTerms(e.target.value)} className="w-full rounded border border-line-strong px-3 py-2 text-sm text-ink outline-none">
              {F.repaymentTermsOptions.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold text-ink" htmlFor="lnd-borrow-venue">Venue</label>
            <select id="lnd-borrow-venue" value={venue} onChange={(e) => setVenue(e.target.value)} className="w-full rounded border border-line-strong px-3 py-2 text-sm text-ink outline-none">
              {F.venueOptions.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>

        <InfoBanner variant="warning" className="mt-5">
          Borrow requires collateral deposit of <strong>{F.collateralAmount} {F.collateralAsset}</strong> before execution. Liquidation occurs if health factor drops below <strong>1.00</strong>. Margin warning triggers at <strong>1.25</strong>.
        </InfoBanner>
      </div>

      <div className="mt-6 flex max-w-4xl items-center justify-end gap-3">
        {!formValid && <span className="text-xs text-muted">Enter borrow and collateral amounts greater than 0 to continue.</span>}
        <Link to="/treasury/lending" className={buttonVariants({ variant: "secondary", size: "sm" })}>Cancel</Link>
        <Button variant="secondary" size="sm" onClick={() => deferTo("Draft Persistence", "Save Draft")}>Save Draft</Button>
        <Button
          size="sm"
          disabled={!formValid || !canCreate}
          onClick={() => {
            if (!canCreate) return;
            setSubmitted(true);
            toast({ variant: "success", title: "Borrow intent submitted for approval", description: localOnly(`${borrowAmount} ${borrowAsset} against ${collateralAmount} ${collateralAsset} collateral via ${venue} — pending policy approval.`) });
          }}
        >
          Submit Borrow Intent
        </Button>
      </div>
      <TreasuryBanner label="LND-02: New Borrow" />
    </div>
  );
}

/* ─── LND-03 Position Detail ───
   Canonical source: specs/06-treasury-operations/mocks/lending-wireframes.html#LND-03
   ("happy" state only). Fixed, non-parameterized route: LND_DETAIL_002 is
   the only position with canonical detail data (health-factor gauge,
   collateral breakdown, repayment schedule) — a dynamic [id] route would
   imply resolving ids that don't exist in the fixture, the same reasoning
   already applied to every other single-fixture Treasury/Staking detail
   screen in this engagement (STK-02, STK-04, TRS-02). Every figure below
   is rendered verbatim from LND_DETAIL_002 — nothing computed, nothing
   simulated, including the health-factor gauge's fill width (a literal
   static value transcribed from the canonical source, not a formula). */
const LND03_REPAY_TONE: Record<string, TreasuryTone> = {
  repaid: "success",
  "due today": "warning",
  upcoming: "neutral",
  "open-ended": "info",
};

export function PositionDetail() {
  const D = LND_DETAIL_002;
  const deferTo = (frame: string, label: string) =>
    toast({
      variant: "info",
      title: `${label} — not implemented`,
      description: `${frame} is deferred to a later treasury task, or references an id that does not exist in the real registry. No navigation was made; nothing was created or executed.`,
    });

  const assetAmountLead = D.assetAmount.split(" (")[0];

  return (
    <div>
      <BackLink href="/treasury/lending">Back to Lending</BackLink>
      <PageHeader title="Position Detail" />

      <InfoBanner variant="warning">
        <strong>Health Factor Warning:</strong> {D.positionId} health factor is {D.healthFactor.current}, below the {D.healthFactor.warning} margin-warning threshold. Last updated: {D.healthFactor.lastUpdated}.
      </InfoBanner>

      <Panel title={`Position ${D.positionId} — ${D.type} ${assetAmountLead}`} bodyClassName="px-5 py-4">
        <dl className="grid grid-cols-2 gap-y-4 gap-x-6 text-sm lg:grid-cols-4">
          <div><dt className="text-xs uppercase tracking-wide text-muted">Position ID</dt><dd className="font-mono font-bold text-ink">{D.positionId}</dd></div>
          <div><dt className="text-xs uppercase tracking-wide text-muted">Type</dt><dd className="font-semibold text-ink">{D.type}</dd></div>
          <div><dt className="text-xs uppercase tracking-wide text-muted">Asset / Amount</dt><dd className="font-semibold text-ink">{D.assetAmount}</dd></div>
          <div><dt className="text-xs uppercase tracking-wide text-muted">Venue</dt><dd className="text-ink">{D.venue}</dd></div>
          <div><dt className="text-xs uppercase tracking-wide text-muted">Initiated By</dt><dd className="text-ink">{D.initiatedBy}</dd></div>
          <div><dt className="text-xs uppercase tracking-wide text-muted">Opened</dt><dd className="text-ink">{D.opened}</dd></div>
          <div><dt className="text-xs uppercase tracking-wide text-muted">Status</dt><dd><ToneBadge tone="warning">{D.statusDisposition}</ToneBadge></dd></div>
          <div><dt className="text-xs uppercase tracking-wide text-muted">Disposition</dt><dd><ToneBadge tone="warning">{D.statusDisposition}</ToneBadge></dd></div>
        </dl>
      </Panel>

      <Panel title="Health Factor" bodyClassName="px-5 py-4">
        <div className="flex items-center gap-4">
          <div className="text-3xl font-bold text-warning">{D.healthFactor.current}</div>
          <div className="flex-1">
            <div className="h-3 overflow-hidden rounded-full bg-bg">
              <div className="h-full rounded-full" style={{ width: `${D.healthFactor.barPct}%`, background: "linear-gradient(90deg, var(--ads-status-danger) 0%, var(--ads-status-warning) 50%, var(--ads-status-success) 100%)" }} />
            </div>
            <div className="mt-1 flex justify-between text-[11px] text-muted">
              <span>{D.healthFactor.liquidation} Liquidation</span>
              <span className="font-semibold text-warning">{D.healthFactor.current} Current</span>
              <span>{D.healthFactor.warning} Warning</span>
              <span>{D.healthFactor.safe} Safe</span>
            </div>
          </div>
        </div>
      </Panel>

      <Panel title="Collateral Breakdown" bodyClassName="p-0">
        <TableWrapper className="rounded-none border-0">
          <Table className="dense">
            <thead><tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted"><th className="px-4 py-2.5">Asset</th><th className="px-4 py-2.5">Amount</th><th className="px-4 py-2.5">Value (USD)</th><th className="px-4 py-2.5">Weight</th></tr></thead>
            <tbody>
              {D.collateral.map((c) => (
                <tr key={c.asset} className="border-b border-line last:border-b-0">
                  <td className="px-4 py-3 text-[13px] font-semibold text-ink">{c.asset}</td>
                  <td className="px-4 py-3 text-[13px] tabular-nums text-text">{c.amount}</td>
                  <td className="px-4 py-3 text-[13px] tabular-nums text-text">{c.valueUsd}</td>
                  <td className="px-4 py-3 text-[13px] tabular-nums text-text">{c.weight}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      </Panel>

      <Panel title="Repayment Schedule" bodyClassName="p-0">
        <TableWrapper className="rounded-none border-0">
          <Table className="dense">
            <thead><tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted"><th className="px-4 py-2.5">Date</th><th className="px-4 py-2.5">Type</th><th className="px-4 py-2.5">Amount</th><th className="px-4 py-2.5">Status</th></tr></thead>
            <tbody>
              {D.repaymentSchedule.map((r, i) => (
                <tr key={i} className="border-b border-line last:border-b-0">
                  <td className="px-4 py-3 text-[13px] text-ink">{r.date}</td>
                  <td className="px-4 py-3 text-[13px] text-text">{r.type}</td>
                  <td className="px-4 py-3 text-[13px] tabular-nums font-semibold text-ink">{r.amount}</td>
                  <td className="px-4 py-3"><ToneBadge tone={LND03_REPAY_TONE[r.status] ?? "neutral"}>{r.status}</ToneBadge></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      </Panel>

      <Panel title="Evidence & Reconciliation" bodyClassName="px-5 py-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted">On-chain Evidence</div>
            <button type="button" className="mt-1 text-sm font-semibold text-primary hover:underline" onClick={() => deferTo("EVD-01", "Evidence Records")}>{D.evidence.count} evidence records →</button>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted">Reconciliation</div>
            <button type="button" className="mt-1 text-sm font-semibold text-primary hover:underline" onClick={() => deferTo("REC-00", "Reconciliation Record")}>{D.evidence.recon} →</button>
          </div>
        </div>
      </Panel>

      <div className="flex flex-wrap items-center gap-2">
        <Link to="/treasury/lending/incident" className={buttonVariants({ size: "sm" })}>View Margin Incident</Link>
        <Link to="/treasury/lending" className={buttonVariants({ size: "sm", variant: "secondary" })}>← Back to Overview</Link>
      </div>

      <TreasuryBanner label="LND-03: Position Detail" />
    </div>
  );
}

/* ─── LND-04 Margin / Liquidation Incident ───
   Canonical source: specs/06-treasury-operations/mocks/lending-wireframes.html#LND-04
   ("happy" state only). Fixed route, single fixture (LND_MARGIN_INCIDENT).
   All 4 mitigation actions (Repay Partial, Add Collateral, Unwind
   Position, Escalate to Treasury Committee) require a real
   approval/execution workflow that does not exist in this app — honest
   deferral for all 4, matching the canonical text's own "All actions
   require approval workflow" framing rather than inventing execution
   behavior or a fake optimistic state transition. */
export function MarginIncident() {
  const I = LND_MARGIN_INCIDENT;
  const deferTo = (frame: string, label: string) =>
    toast({
      variant: "info",
      title: `${label} — not implemented`,
      description: `${frame} is deferred to a later treasury task, or references an id that does not exist in the real registry. No navigation was made; nothing was created or executed.`,
    });

  return (
    <div>
      <BackLink href="/treasury/lending">Back to Lending</BackLink>
      <PageHeader title="Margin Incident" />

      <InfoBanner variant="danger">
        <strong>Margin Incident — LND-002:</strong> Health factor dropped to {I.currentHealthFactor} from 1.45. Triggered at {I.triggeredAt}. Assigned to: {I.assignedTo}.
      </InfoBanner>

      <Panel title="Incident Summary" bodyClassName="px-5 py-4">
        <dl className="grid grid-cols-2 gap-y-4 gap-x-6 text-sm lg:grid-cols-4">
          <div><dt className="text-xs uppercase tracking-wide text-muted">Position</dt><dd className="font-semibold text-ink">{I.position}</dd></div>
          <div><dt className="text-xs uppercase tracking-wide text-muted">Venue</dt><dd className="text-ink">{I.venue}</dd></div>
          <div><dt className="text-xs uppercase tracking-wide text-muted">Current Health Factor</dt><dd className="font-bold text-warning">{I.currentHealthFactor}</dd></div>
          <div><dt className="text-xs uppercase tracking-wide text-muted">Previous Health Factor</dt><dd className="text-ink">{I.previousHealthFactor}</dd></div>
          <div><dt className="text-xs uppercase tracking-wide text-muted">Liquidation Threshold</dt><dd className="font-bold text-danger">{I.liquidationThreshold}</dd></div>
          <div><dt className="text-xs uppercase tracking-wide text-muted">Collateral</dt><dd className="text-ink">{I.collateral}</dd></div>
          <div><dt className="text-xs uppercase tracking-wide text-muted">Trigger</dt><dd className="text-ink">{I.trigger}</dd></div>
          <div><dt className="text-xs uppercase tracking-wide text-muted">Assigned To</dt><dd className="text-ink">{I.assignedTo}</dd></div>
        </dl>
      </Panel>

      <Panel title="Health Factor Timeline" bodyClassName="px-5 py-4">
        <ol className="space-y-3">
          {I.timeline.map((step, i) => (
            <li key={i} className="flex gap-3">
              <span className={cn("mt-1.5 size-2 shrink-0 rounded-full", step.tone === "danger" ? "bg-danger" : step.tone === "warning" ? "bg-warning" : "bg-disabled")} aria-hidden />
              <div>
                <div className="text-xs text-muted">{step.ts}</div>
                <div className="text-sm text-ink">{step.desc}</div>
              </div>
            </li>
          ))}
        </ol>
      </Panel>

      <Panel title="Available Actions" bodyClassName="px-5 py-4">
        <p className="mb-3 text-sm text-muted">Select an action to mitigate the margin risk for {I.position.split(" — ")[0]}.</p>
        <div className="flex flex-wrap gap-2">
          {I.actions.map((a) => (
            <Button key={a} size="sm" variant={a === "Unwind Position" ? "destructive" : a === "Escalate to Treasury Committee" ? "secondary" : "primary"} onClick={() => deferTo("Lending Execution", a)}>{a}</Button>
          ))}
        </div>
        <div className="mt-3 text-xs text-muted">
          {I.actionsNote}{" "}
          <button type="button" className="font-semibold text-primary hover:underline" onClick={() => deferTo("APR-01", "Approval Workflow")}>approval workflow</button>
          {" "}· View in{" "}
          <button type="button" className="font-semibold text-primary hover:underline" onClick={() => deferTo("MON-05", "Monitoring Alert")}>Monitoring →</button>
        </div>
      </Panel>

      <div className="flex flex-wrap items-center gap-2">
        <Link to="/treasury/lending/position" className={buttonVariants({ size: "sm", variant: "secondary" })}>← Position Detail</Link>
        <Link to="/treasury/lending" className={buttonVariants({ size: "sm", variant: "secondary" })}>← Overview</Link>
      </div>

      <TreasuryBanner label="LND-04: Margin Incident" />
    </div>
  );
}

/* ─── TRS-03 Route Comparison ───
   Canonical source: specs/06-treasury-operations/mocks/treasury-home-wireframes.html#TRS-03
   ("happy" state only — exception/permission/degraded/empty/readonly state
   tabs exist in the wireframe but are not implemented here; see the TRS-03
   audit section for the reasoning). Reuses TRS_ROUTES/TRS_ROUTE_INTRO — the
   Task-1 foundation data purpose-built for this exact screen but never
   wired up until now (the prior RouteComparison() here was a pre-audit
   prototype port driven by unrelated custody-transfer-route fixtures,
   fully replaced). The compared intent shares its amount/source wallet
   with TRS_INTENT_0051 (TRS-02), but the canonical TRS-03 frame contains
   no link to the TRS-02 intent-detail screen, so none was invented. */
const TRS03_RISK_TONE: Record<"Low" | "High", TreasuryTone> = { Low: "success", High: "danger" };
const TRS03_POLICY_TONE: Record<"Pass" | "Fail", TreasuryTone> = { Pass: "success", Fail: "danger" };

export function RouteComparison() {
  const blockedRoute = TRS_ROUTES.find((r) => r.blocked);
  const deferTo = (frame: string, label: string) =>
    toast({
      variant: "info",
      title: `${label} — not implemented`,
      description: `${frame} is deferred to a later treasury task, or references an id that does not exist in the real registry. No navigation was made; nothing was created or executed.`,
    });

  return (
    <div>
      <BackLink href="/treasury">Back to Treasury Home</BackLink>
      <PageHeader title="Route Comparison" subtitle={'Compare execution routes for "Stake 500 ETH" across Mesh, custody, exchange, OTC/RFQ, and DeFi.'} />

      <Panel bodyClassName="px-5 py-4">
        <div className="mb-1 text-[15px] font-bold text-ink">Intent: {TRS_ROUTE_INTRO.intent}</div>
        <div className="text-sm text-muted">
          Source: <Link to={`/wallets/${TRS_ROUTE_INTRO.sourceWallet}`} className="font-mono font-semibold text-primary hover:underline">{TRS_ROUTE_INTRO.sourceWallet}</Link> · Comparing {TRS_ROUTE_INTRO.comparing}
        </div>
      </Panel>

      <TableWrapper className="mb-5">
        <Table className="dense">
          <thead><tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted"><th className="px-4 py-2.5">Route</th><th className="px-4 py-2.5">Provider</th><th className="px-4 py-2.5">APY</th><th className="px-4 py-2.5">Fee</th><th className="px-4 py-2.5">Lock Period</th><th className="px-4 py-2.5">Risk</th><th className="px-4 py-2.5">Policy</th><th className="px-4 py-2.5 text-right">Action</th></tr></thead>
          <tbody>
            {TRS_ROUTES.map((r) => (
              <tr key={r.id} className="border-b border-line last:border-b-0">
                <td className="px-4 py-3 text-[13px] font-semibold text-ink">
                  <div className="flex items-center gap-2">
                    {r.recommended && <ToneBadge tone="success">Recommended</ToneBadge>}
                    <span>{r.route}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-[13px] text-text">{r.provider}</td>
                <td className="px-4 py-3 text-[13px] font-bold tabular-nums text-success">{r.apy}</td>
                <td className="px-4 py-3 text-[13px] tabular-nums text-text">{r.fee}</td>
                <td className="px-4 py-3 text-[13px] text-text">{r.lock}</td>
                <td className="px-4 py-3"><ToneBadge tone={TRS03_RISK_TONE[r.risk]}>{r.risk}</ToneBadge></td>
                <td className="px-4 py-3"><ToneBadge tone={TRS03_POLICY_TONE[r.policy]}>{r.policy}</ToneBadge></td>
                <td className="px-4 py-3 text-right">
                  {r.blocked ? (
                    <Button size="sm" variant="secondary" disabled title={r.blockedBy ? `Blocked by ${r.blockedBy}` : "Blocked"}>Blocked</Button>
                  ) : (
                    <Button size="sm" variant={r.recommended ? "primary" : "secondary"} onClick={() => deferTo("Route Execution", "Select Route")}>Select</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>

      <InfoBanner variant="info">
        <strong>Policy note:</strong> Direct validator route blocked by {blockedRoute?.blockedBy} (no self-hosted validators without dedicated DevOps approval).{" "}
        <button type="button" className="font-semibold underline" onClick={() => deferTo(blockedRoute?.blockedBy ?? "GOV-STK-02", "Policy Record")}>View policy →</button>
      </InfoBanner>

      <TreasuryBanner label="TRS-03: Route Comparison" />
    </div>
  );
}

/* ─── TRS-04 Treasury Calendar ───
   Canonical source: specs/06-treasury-operations/mocks/treasury-home-wireframes.html#TRS-04
   ("happy" state only — exception/permission/degraded/empty/readonly state
   tabs exist in the wireframe but are not implemented here; see the TRS-04
   audit section for the reasoning). Reuses TRS_CALENDAR (Task-1 foundation)
   directly for the Calendar Summary table (date/type/asset/amount/via map
   1:1 onto the canonical table's columns) and for the timeline's headline/
   narrative/wallet fields (added additively in this task — see that
   field's own doc-comment in treasury-data.ts). TRS_CALENDAR's pre-existing
   `status`/`detail` fields carry *exception*-state framing for the Sep-16
   event ("Overdue" + escalation text) and are intentionally NOT rendered
   here, since the canonical happy state shows no status/overdue framing at
   all — using them would conflate the exception state into the happy view. */
const TRS04_TYPE_TONE: Record<TrsCalendarEvent["type"], TreasuryTone> = {
  Settlement: "info", Unstaking: "success", Maturity: "info", Review: "neutral",
};

export function TreasuryCalendar() {
  const deferTo = (frame: string, label: string) =>
    toast({
      variant: "info",
      title: `${label} — not implemented`,
      description: `${frame} is deferred to a later treasury task, or references an id that does not exist in the real registry. No navigation was made; nothing was created or executed.`,
    });

  return (
    <div>
      <BackLink href="/treasury">Back to Treasury Home</BackLink>
      <PageHeader title="Treasury Calendar" subtitle="Upcoming maturity, unstaking, settlement, review, and repayment events." />

      <div className="mb-3 text-base font-bold text-ink">Upcoming Events — September 2026</div>
      <ol className="mb-6 space-y-4">
        {TRS_CALENDAR.map((ev) => (
          <li key={ev.date} className="flex gap-4">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full border border-line bg-surface text-sm font-bold text-ink" aria-hidden>
              {ev.date.split(" ")[1]}
            </div>
            <div>
              <div className="text-sm font-bold text-ink">{ev.date} — {ev.headline}</div>
              <div className="mt-0.5 text-sm text-text">
                {ev.narrative}
                {ev.wallet && (
                  <>
                    {" "}
                    <Link to={`/wallets/${ev.wallet}`} className="font-mono font-semibold text-primary hover:underline">{ev.wallet}</Link>.
                  </>
                )}
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <ToneBadge tone={TRS04_TYPE_TONE[ev.type]}>{ev.type}</ToneBadge>
                {ev.type === "Unstaking" && (
                  <Link to="/treasury/staking" className="text-xs font-semibold text-primary hover:underline">View position →</Link>
                )}
                {ev.type === "Maturity" && (
                  <button type="button" className="text-xs font-semibold text-primary hover:underline" onClick={() => deferTo("LND-00", "Lending Position")}>View position →</button>
                )}
                {ev.type === "Review" && (
                  <Link to="/governance" className="text-xs font-semibold text-primary hover:underline">Governance →</Link>
                )}
              </div>
            </div>
          </li>
        ))}
      </ol>

      <div className="mb-3 text-base font-bold text-ink">Calendar Summary</div>
      <TableWrapper>
        <Table className="dense">
          <thead><tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted"><th className="px-4 py-2.5">Date</th><th className="px-4 py-2.5">Event Type</th><th className="px-4 py-2.5">Asset</th><th className="px-4 py-2.5">Amount</th><th className="px-4 py-2.5">Counterparty</th></tr></thead>
          <tbody>
            {TRS_CALENDAR.map((ev) => (
              <tr key={ev.date} className="border-b border-line last:border-b-0">
                <td className="px-4 py-3 text-[13px] font-semibold text-ink">{ev.date}</td>
                <td className="px-4 py-3"><ToneBadge tone={TRS04_TYPE_TONE[ev.type]}>{ev.type}</ToneBadge></td>
                <td className="px-4 py-3 text-[13px] text-text">{ev.asset}</td>
                <td className="px-4 py-3 text-[13px] tabular-nums text-text">{ev.amount}</td>
                <td className="px-4 py-3 text-[13px] text-text">{ev.via}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>

      <TreasuryBanner label="TRS-04: Treasury Calendar" />
    </div>
  );
}
